import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    upToMembers: (memberCount: number) => string;
    storage: (storage: string) => string;
    storageWithIncrement: (baseStorage: string, additionalStorage: string) => string;
    upToProjects: (projectCount: number) => string;
    cameraToCloud: string;
    transcription: string;
    unlimitedProjects: string;
    customBrandedShares: string;
    passphraseProtectedShares: string;
    shareLinkExpiration: string;
    sharePermissions: string;
    commentAttachments: string;
    restrictedProjects: string;
    restrictedFolders: string;
    internalComments: string;
    customMembersStorage: string;
    singleSignOn: string;
    multipleWorkspaces: string;
    secureSharing: string;
    sessionBasedWatermarking: string;
    forensicWatermarking: string;
    assetLifecycleManagement: string;
    digitalRightsManagement: string;
    mountedStorage: (storage: string) => string;
    mountedStorageIncluded: string;
    storageConnect: string;
    priorityInAppSupport: string;
    dedicatedAccountTeam: string;
    periods: {
        /** Monthly billing period abbreviation */
        monthly: string;
        /** Semi-annual billing period label */
        semiannually: string;
        /** Yearly billing period abbreviation */
        yearly: string;
    };
};
export type Deps = {
    enterpriseMountedStorageLimit: any;
};
export default {
    DEFINE(_messages: Text, { enterpriseMountedStorageLimit }: Deps): void {
        return Object.defineProperties(this, {
            /** Feature text for up to X members on a plan. Use {memberCount} for the number of members */
            upToMembers: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (memberCount: number): string => __intl__.intl().formatMessage({ id: "PlanConstants.upToMembers?c=30eeqedm2xlzi", defaultMessage: "Up to {memberCount} Members" }, { memberCount: memberCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for storage display. Use {storage} for formatted storage amount */
            storage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (storage: string): string => __intl__.intl().formatMessage({ id: "PlanConstants.storage?c=3pjwuea5dt4ee", defaultMessage: "{storage} Storage" }, { storage: storage }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for storage with per-user increment. Use {baseStorage} for base amount, {additionalStorage} for per-user amount */
            storageWithIncrement: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (baseStorage: string, additionalStorage: string): string => __intl__.intl().formatMessage({ id: "PlanConstants.storageWithIncrement?c=2e5yyauk4ifvi", defaultMessage: "{baseStorage} Included + {additionalStorage} per additional Member" }, { baseStorage: baseStorage, additionalStorage: additionalStorage }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for up to X projects. Use {projectCount} for the number of projects */
            upToProjects: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (projectCount: number): string => __intl__.intl().formatMessage({ id: "PlanConstants.upToProjects?c=3rwba97wp5er1", defaultMessage: "Up to {projectCount} projects" }, { projectCount: projectCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for Camera to Cloud */
            cameraToCloud: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.cameraToCloud?c=hfmwmmyolnyf", defaultMessage: "Camera to Cloud" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for Transcription and captions */
            transcription: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.transcription?c=3he57u1e3x3eg", defaultMessage: "Transcription and captions" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for unlimited projects */
            unlimitedProjects: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.unlimitedProjects?c=3n7m3pbhbftuc", defaultMessage: "Unlimited Projects" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for custom-branded shares */
            customBrandedShares: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.customBrandedShares?c=2mk60natasbc7", defaultMessage: "Custom-Branded Shares" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for passphrase-protected shares */
            passphraseProtectedShares: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.passphraseProtectedShares?c=1vvzkg0qnvh1m", defaultMessage: "Passphrase-Protected Shares" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for share link expiration */
            shareLinkExpiration: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.shareLinkExpiration?c=1ulq64gfuic0h", defaultMessage: "Share Link Expiration" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for share permissions */
            sharePermissions: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.sharePermissions?c=1d64ys2k1jmiv", defaultMessage: "Share Permissions" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for comment attachments */
            commentAttachments: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.commentAttachments?c=3saamj7e9jb2b", defaultMessage: "Comment Attachments" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for restricted projects */
            restrictedProjects: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.restrictedProjects?c=9zwqbyaxm9fv", defaultMessage: "Restricted Projects" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for restricted folders */
            restrictedFolders: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.restrictedFolders?c=1ow3sq3dm8dts", defaultMessage: "Restricted Folders" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for internal comments */
            internalComments: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.internalComments?c=3gbqclspvx0ye", defaultMessage: "Internal Comments" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for custom members and storage */
            customMembersStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.customMembersStorage?c=p04gfje4hljf", defaultMessage: "Custom Members & Storage" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for single sign-on */
            singleSignOn: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.singleSignOn?c=3k683u3o6lzp2", defaultMessage: "Single Sign-On" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for multiple workspaces */
            multipleWorkspaces: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.multipleWorkspaces?c=2plpff7wqupzd", defaultMessage: "Multiple Workspaces" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for secure sharing */
            secureSharing: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.secureSharing?c=28k17ztxzjsbw", defaultMessage: "Secure Sharing" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for session-based watermarking */
            sessionBasedWatermarking: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.sessionBasedWatermarking?c=13y84bfm8yiuk", defaultMessage: "Session-Based Watermarking" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for forensic watermarking */
            forensicWatermarking: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.forensicWatermarking?c=2eskpih1npd1t", defaultMessage: "Forensic Watermarking" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for asset lifecycle management */
            assetLifecycleManagement: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.assetLifecycleManagement?c=1uk6z4czwyph1", defaultMessage: "Asset Lifecycle Management" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for Digital Rights Management (DRM) */
            digitalRightsManagement: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.digitalRightsManagement?c=2r1zdl845yzzl", defaultMessage: "Digital Rights Management (DRM)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for mounted storage. Use {storage} for formatted storage amount */
            mountedStorage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (storage: string): string => __intl__.intl().formatMessage({ id: "PlanConstants.mountedStorage?c=24as72gf7n29u", defaultMessage: "{storage} Mounted Storage" }, { storage: storage }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for mounted storage included (enterprise) */
            mountedStorageIncluded: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.mountedStorageIncluded?c=2ujhthvbhgvps", defaultMessage: "{enterpriseMountedStorageLimit} Mounted Storage" }, { enterpriseMountedStorageLimit: enterpriseMountedStorageLimit }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for Storage Connect */
            storageConnect: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.storageConnect?c=1vpfe7gh85moh", defaultMessage: "Storage Connect" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for priority in-app support */
            priorityInAppSupport: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.priorityInAppSupport?c=1gue33b0nnbtv", defaultMessage: "Priority In-App Support" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature text for dedicated account team */
            dedicatedAccountTeam: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.dedicatedAccountTeam?c=3ofrizmun1e2j", defaultMessage: "Dedicated Account Team" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Billing period labels */
            periods: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Monthly billing period abbreviation */
                        monthly: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.periods.monthly?c=qqve62mo3v60", defaultMessage: "mo." }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Semi-annual billing period label */
                        semiannually: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.periods.semiannually?c=2zg4f259g8qku", defaultMessage: "six months" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Yearly billing period abbreviation */
                        yearly: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanConstants.periods.yearly?c=cfo2gz86dz38", defaultMessage: "yr." }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
